import platform, { analytics } from '@play-co/gcinstant';
import { ShareAsyncOptions } from '@play-co/gcinstant';
import { withTiming } from '@play-co/gcinstant/build/esm/asyncUtils';
import { share } from '@play-co/gcinstant/build/esm/web/share';
import { LinkRefData } from 'src/replicant/chatbot';
import { assertNever } from 'src/replicant/utils';
import StateObserver from 'src/StateObserver';
import { encode } from 'src/replicant/utils/base64';

interface MessengerShareOptions extends ShareAsyncOptions {
  /** Configures the introductory play button message sent to users who click this share. */
  chatbot?: {
    messageText?: string;
    imageUrl?: string;
    buttonText?: string;
  };

  /** Whether to share using the native share sheet (navigator.share) or directly to Messenger (intent URL). */
  method: 'native' | 'direct';
}

export function createLinkToMessenger(refData: LinkRefData): string {
  let urlStr = 'https://m.me/119274131131538'; // Trembly Game

  if (process.env.IS_DEVELOPMENT) {
    urlStr = 'https://m.me/110782358580421'; // Word Frenzy
  }

  const url = new URL(urlStr);
  url.searchParams.set('ref', encode(JSON.stringify(refData)));
  return url.href;
}

export function redirectToMessenger() {
  const link = createLinkToMessenger({
    type: 'LANDING',
    messageText: 'Continue playing by clicking the button below!',
  });
  location.href = link;
}

function getRandomLinkId(length = 8): string {
  const base62 =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

  let str = '';
  for (let ii = 0; ii < length; ++ii) {
    str += base62.charAt((Math.random() * 62) >>> 0);
  }
  return str;
}

function createShortUrl(linkId: string, body: unknown): string {
  // The domain to use for the short link
  const shortDomain = !location.port
    ? location.hostname
    : 'localhost.playco.games';

  // The short URL we're about to create
  const shortUrl = `https://${shortDomain}/share/${linkId}`;

  // Post to the backend in the background
  fetch(shortUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch((error) => {
    analytics.pushError('WebCreateShortURLFailure', error, {
      webShareLinkId: linkId,
    });
  });

  return shortUrl;
}

/** Shares a link that redirects to the Messenger chatbot, with Opengraph and virality analytics. */
export async function shareLinkToMessenger(
  opts: MessengerShareOptions,
): Promise<void> {
  const userId = StateObserver.getState().user.id;
  const fullPayload = (platform as any)._populateNotificationPayload(
    opts.data,
    'INVITE',
  );
  const linkId = getRandomLinkId();

  const payload = {
    playerID: fullPayload.playerID,
    feature: fullPayload.feature,
    $subFeature: fullPayload.$subFeature,
    $channel: fullPayload.$channel,
    webShareLinkId: linkId,
    webShareMethod: opts.method,
  };

  // Create the long ig.me link
  const longUrl = createLinkToMessenger({
    type: 'SHARE',
    payload,
    messageText: opts.chatbot?.messageText,
    imageUrl: opts.chatbot?.imageUrl,
    buttonText: opts.chatbot?.buttonText,
  });
  console.log('Long URL:', longUrl);

  // Create the short mygame.com/share/abc123 link
  const shortUrl = createShortUrl(linkId, {
    url: longUrl,
    // Convert relative URL paths to absolute paths
    image: opts.image ? new URL(opts.image, location.href).href : undefined,
    title: opts.title,
    description: opts.description,
    amplitudeId: process.env.AMPLITUDE,
    userId,
    eventProps: payload,
  });
  console.log('Short URL:', shortUrl);

  analytics.pushEvent('ShareAsync', payload);

  switch (opts.method) {
    case 'native':
      try {
        const { requestMs } = await withTiming(() =>
          share({ text: opts.text, url: shortUrl }),
        );
        analytics.pushEvent('ShareAsyncSuccess', { ...payload, requestMs });
      } catch ({ error, requestMs }) {
        analytics.pushError('ShareAsyncFailed', error, {
          ...payload,
          requestMs,
        });
        throw error;
      }
      break;

    case 'direct': {
      const intentUrl = new URL('fb-messenger://share/');
      intentUrl.searchParams.set('link', shortUrl);
      window.open(intentUrl.href, '_blank', 'noopener');
      analytics.pushEvent('ShareAsyncSuccess', payload);
      break;
    }

    default:
      assertNever(opts.method);
  }
}
